import { useRef, useState } from "react";

/** Lightweight, interactive SVG/CSS charts matching the dash.css design system. */

export const PLAN_COLORS: Record<string, string> = {
  free: "oklch(62% 0.02 260)",
  pro: "oklch(82% 0.16 165)",
  scale: "oklch(70% 0.15 245)",
  business: "oklch(68% 0.15 200)",
  enterprise: "oklch(72% 0.16 300)",
  custom: "oklch(80% 0.15 75)",
};

export interface ChartPoint {
  label: string;
  value: number;
}

const VW = 900;

export function AreaChart({ data, height = 220 }: { data: ChartPoint[]; height?: number }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hi, setHi] = useState<number | null>(null);
  if (!data.length) return <ChartEmpty height={height} />;

  const w = VW;
  const h = height;
  const padL = 52;
  const padR = 14;
  const padT = 14;
  const padB = 30;
  const chartW = w - padL - padR;
  const chartH = h - padT - padB;
  const max = Math.max(...data.map((d) => d.value), 1);
  const denom = data.length > 1 ? data.length - 1 : 1;

  const pts = data.map((d, i) => ({
    x: padL + (i / denom) * chartW,
    y: padT + chartH - (d.value / max) * chartH,
  }));
  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const fillPath = `${linePath} L${pts[pts.length - 1]!.x.toFixed(1)},${(padT + chartH).toFixed(1)} L${pts[0]!.x.toFixed(1)},${(padT + chartH).toFixed(1)} Z`;

  const gridCount = 4;
  const gridLines = Array.from({ length: gridCount + 1 }, (_, i) => {
    const val = (max / gridCount) * i;
    return { y: padT + chartH - (val / max) * chartH, val: Math.round(val) };
  });
  const labelIdxs = pickEvenlyIdx(data.length, 7);

  function onMove(e: React.MouseEvent) {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xf = (e.clientX - rect.left) / rect.width;
    const cs = padL / w;
    const ce = (w - padR) / w;
    const t = Math.min(1, Math.max(0, (xf - cs) / (ce - cs)));
    setHi(Math.round(t * (data.length - 1)));
  }

  return (
    <div className="chart-wrap" ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setHi(null)}>
      <svg viewBox={`0 0 ${w} ${h}`} className="chart" preserveAspectRatio="none" style={{ height }}>
        <defs>
          <linearGradient id="adminMintFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="oklch(82% 0.16 165)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="oklch(82% 0.16 165)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {gridLines.map(({ y, val }) => (
          <g key={`${val}-${y}`}>
            <line x1={padL} y1={y} x2={w - padR} y2={y} className="grid-line" />
            <text x={padL - 8} y={y + 3} textAnchor="end" className="axis-label">{compact(val)}</text>
          </g>
        ))}
        <path d={fillPath} className="area-fill" fill="url(#adminMintFade)" />
        <path d={linePath} className="area-line" />
        {hi != null && (
          <>
            <line x1={pts[hi]!.x} y1={padT} x2={pts[hi]!.x} y2={padT + chartH} className="guide-line" />
            <circle cx={pts[hi]!.x} cy={pts[hi]!.y} r="4" className="hover-dot" />
          </>
        )}
        {labelIdxs.map((idx) => (
          <text key={idx} x={pts[idx]!.x} y={h - 8} textAnchor="middle" className="axis-label">{data[idx]!.label}</text>
        ))}
      </svg>
      {hi != null && (
        <div className="chart-tip" style={{ left: `${(pts[hi]!.x / w) * 100}%`, top: pts[hi]!.y }}>
          <span className="t-label">{data[hi]!.label}</span>
          <span className="t-val">{data[hi]!.value.toLocaleString("en-US")}</span>
        </div>
      )}
    </div>
  );
}

interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

export function DonutChart({ slices, onSelect }: { slices: DonutSlice[]; onSelect?: (key: string) => void }) {
  const [hi, setHi] = useState<string | null>(null);
  const total = slices.reduce((s, x) => s + x.value, 0);
  const r = 54;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const active = hi ? slices.find((s) => s.key === hi) : null;

  return (
    <div className="donut-wrap">
      <svg viewBox="0 0 140 140" className="donut" width="140" height="140">
        <circle cx="70" cy="70" r={r} className="donut-track" fill="none" strokeWidth="18" />
        {total > 0 &&
          slices.map((s) => {
            const frac = s.value / total;
            const dash = frac * c;
            const seg = (
              <circle
                key={s.key}
                cx="70"
                cy="70"
                r={r}
                fill="none"
                strokeWidth={hi === s.key ? 22 : 18}
                stroke={s.color}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 70 70)"
                style={{ transition: "stroke-width .12s", cursor: onSelect ? "pointer" : "default" }}
                onMouseEnter={() => setHi(s.key)}
                onMouseLeave={() => setHi(null)}
                onClick={() => onSelect?.(s.key)}
              />
            );
            offset += dash;
            return seg;
          })}
        <text x="70" y="66" textAnchor="middle" className="donut-total">{compact(active ? active.value : total)}</text>
        <text x="70" y="84" textAnchor="middle" className="donut-sub">{active ? active.label : "requests"}</text>
      </svg>
      <ul className="legend">
        {slices.map((s) => (
          <li
            key={s.key}
            className={onSelect ? "clickable" : undefined}
            onMouseEnter={() => setHi(s.key)}
            onMouseLeave={() => setHi(null)}
            onClick={() => onSelect?.(s.key)}
          >
            <span className="dot" style={{ background: s.color }} />
            <span className="lg-label">{s.label}</span>
            <span className="lg-val tnum">{total > 0 ? Math.round((s.value / total) * 100) : 0}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface HBarItem {
  id?: string;
  label: string;
  value: number;
}

export function HBars({ items, onSelect }: { items: HBarItem[]; onSelect?: (id: string) => void }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  if (!items.length) return <ChartEmpty height={120} />;
  return (
    <div className="hbars">
      {items.map((it, i) => {
        const clickable = !!(onSelect && it.id);
        return (
          <div
            className={`hbar-row${clickable ? " clickable" : ""}`}
            key={it.id ?? i}
            onClick={() => clickable && onSelect!(it.id!)}
            title={`${it.label}: ${it.value.toLocaleString("en-US")}`}
          >
            <span className="hbar-label">{it.label}</span>
            <span className="hbar-track">
              <span className="hbar-fill" style={{ width: `${(it.value / max) * 100}%` }} />
            </span>
            <span className="hbar-val tnum">{compact(it.value)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function Sparkline({ values, height = 38 }: { values: number[]; height?: number }) {
  if (!values.length) return null;
  const w = 160;
  const h = height;
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  const denom = values.length > 1 ? values.length - 1 : 1;
  const pts = values.map((v, i) => ({
    x: (i / denom) * w,
    y: h - 4 - ((v - min) / range) * (h - 8),
  }));
  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const fillPath = `${linePath} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="spark" style={{ height }}>
      <path d={fillPath} className="fill" />
      <path d={linePath} className="line" />
    </svg>
  );
}

function ChartEmpty({ height }: { height: number }) {
  return (
    <div className="chart-empty" style={{ height }}>
      No data for this range yet.
    </div>
  );
}

function pickEvenlyIdx(n: number, k: number): number[] {
  if (n <= k) return Array.from({ length: n }, (_, i) => i);
  const step = (n - 1) / (k - 1);
  return Array.from({ length: k }, (_, i) => Math.round(i * step));
}

/** Compact number formatting: 1234 → 1.2k, 1_200_000 → 1.2M. */
export function compact(n: number): string {
  if (!Number.isFinite(n)) return "∞";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return String(Math.round(n));
}
